import { useId } from 'react'

import {
  Box,
  SegmentedControl,
  type SegmentedControlProps,
} from '@enechain/ecloud-designsystem'

type Props<T extends string> = SegmentedControlProps<T> & {
  /**
   * 択一群の accessible name (必須)。 DS `SegmentedControl` は aria 系 prop を
   * 受け取らないため、 グループ名はこのラッパの `role="radiogroup"` に持たせる。
   * 隣に置いたラベル (FilterSection 等) は兄弟要素なのでグループには関連付かず、
   * 名前を省くと「何の択一か」が a11y tree から失われるので必須とする。
   */
  'aria-label': string
}

/**
 * a11y 補正付きの `SegmentedControl` (DS `SegmentedControl` の薄いラッパ)。
 *
 * [DS 差異] DS `SegmentedControlRadio` は実体の `input[type="radio"]` を
 * **inline style の `display: none`** で隠すため、 キーボード focus も a11y tree への
 * 露出も失われる (WCAG 2.1.1 / 4.1.2 を満たせない)。 inline style は author の
 * `!important` でしか上書きできないので、 ここで visually-hidden へ差し替えて
 * 操作性だけを戻す:
 *
 * - `display: none` と同じく position: absolute で flow から外れるため、 トラック・
 *   セグメント・影・寸法は DS のまま変わらない
 * - label が input を内包しているので accessible name は自動で付く
 * - `name` 共有の native radio group なので Tab 1 stop + 矢印キー移動が戻る
 *
 * focus 可視化は DS 側に無いので、 DS `button.recipe.ts` と同じ `Focus.clarity` の
 * リングを input の次兄弟 (= セグメント本体) に描く。 `boxShadow` ではなく `outline` を
 * 使うのは、 選択セグメントの `shadSm` (DS が boxShadow で描く) を潰さないため。
 *
 * 注意 (触る前に読むこと):
 * - **`display: 'block !important'` の `!important` は DS の inline style を破る唯一の
 *   手段なので外さないこと。** 外すと inline の `display: none` が勝ち、 補正が丸ごと無効化する
 *   (見た目は変わらないので気付けない)。
 * - **`pointer-events: none` を足さないこと。** visually-hidden の定番として付けたくなるが、
 *   testing-library / userEvent から radio をクリックできなくなる (#4373 で実際に踏んだ)。
 * - focus リングの selector は DS の内部 DOM 形状 (`label > Box > input + Flex`) に依存する。
 *   形が変わると **見た目は変わらないままリングだけ静かに消える** (各画面の test で
 *   「radio の次兄弟がセグメント本体である」ことを固定している)。
 *
 * 本来の直しは DS 側で radio を visually-hidden 化すること
 * (enechain/eclair-designsystem#1216 起票済み)。 **DS が直ったら本ラッパごと削除し、
 * 各呼び出し側を DS `SegmentedControl` + 素の aria-label に戻してよい。**
 *
 * ## `name` を実体ごとに一意化する理由
 *
 * DS `SideMenuLayout` は**ページ全体をモバイル用にもう 1 組 `display: none` で描画する**。
 * そのため呼び出し側が渡した `name` をそのまま使うと、 同名の native radio group が
 * document 内に 2 組できる。 native radio group は「同名で checked は 1 つだけ」なので、
 * 可視側を選択した瞬間に非表示側の `checked` が外れる / その逆が起き、 **可視側の
 * radiogroup から `checked` の radio が消える** (2026-07-31 実測: 48点データの売買切替と
 * 時間内入札のサブタブで 100% 再現)。 見た目 (白地セグメント) は DS が `selected` prop で
 * 描くため正しく動き、 **目視では気付けない**。
 *
 * `useId()` は同じ component でも実体ごとに別の値を返すため、 複製された 2 組が別グループに
 * なり干渉しなくなる。 呼び出し側から見た `name` の意味 (= フォーム上の識別子) は変わらない。
 */
export const AccessibleSegmentedControl = <T extends string>({
  'aria-label': ariaLabel,
  name,
  ...segmentedControlProps
}: Props<T>): React.ReactElement => {
  const instanceId = useId()

  return (
    <Box
      aria-label={ariaLabel}
      css={{
        "& input[type='radio']": {
          border: 0,
          clip: 'rect(0, 0, 0, 0)',
          display: 'block !important',
          height: '1px',
          margin: 0,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        },
        "& input[type='radio']:focus-visible + *": {
          outlineColor: 'Focus.clarity',
          outlineOffset: '0px',
          outlineStyle: 'solid',
          outlineWidth: '2px',
        },
      }}
      role="radiogroup"
    >
      <SegmentedControl
        {...segmentedControlProps}
        name={`${name}-${instanceId}`}
      />
    </Box>
  )
}
